/**
 * useToolCatalog — Facade hook over ToolRegistry.
 *
 * Components use this hook instead of importing ToolRegistry directly,
 * keeping the layer direction: UI → hooks → stores → engine.
 *
 * This is a thin wrapper — NOT a new store. ToolRegistry is populated
 * once at app startup and is treated as static for the app's lifetime.
 */

import { useMemo } from 'react';

import { ToolRegistry } from '@/engine/ToolRegistry';
import type { ITool } from '@/interfaces/ITool';

export interface ToolCatalog {
  /** Look up a tool definition by its unique ID. */
  getToolById: (id: string) => ITool | undefined;
  /** Return all registered tools. */
  getAllTools: () => ITool[];
  /** Return tools belonging to a specific category (defensive copy). */
  getToolsByCategory: (category: string) => ITool[];
  /** Text search across tool name, description, category, and keywords. */
  searchTools: (query: string) => ITool[];
  /** Total number of registered tools. */
  getToolCount: () => number;
}

/**
 * Provides read-only access to the tool catalog.
 *
 * Returns a stable object reference (via useMemo) whose methods
 * delegate to the singleton ToolRegistry at call time.
 */
export function useToolCatalog(): ToolCatalog {
  return useMemo<ToolCatalog>(() => ({
    getToolById: (id: string) => ToolRegistry.get(id),
    getAllTools: () => ToolRegistry.getAll(),
    getToolsByCategory: (category: string) => [...ToolRegistry.getByCategory(category)],
    searchTools: (query: string) => ToolRegistry.search(query),
    getToolCount: () => ToolRegistry.count,
  }), []);
}
